import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import PageLayout from '../components/ui/PageLayout';
import { useToast } from '../components/Toast/ToastContext';
import { buttonClass } from '../components/ui/Button';
import Checkbox from '../components/ui/Checkbox';
import { FieldRow } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import Panel from '../components/ui/Panel';
import LogoSpinner from '../components/ui/LogoSpinner';

const GoldStars = () => {
    const history = useHistory();
    const api = useApi();
    const toast = useToast();

    const [users, setUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [starqty, setStarqty] = useState(1);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Fetch active users for the picker
    useEffect(() => {
        api.get('/users/active')
            .then(data => {
                setUsers(data || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching users:', error);
                toast.error('Failed to load users');
                setLoading(false);
            });
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedUserIds.length === 0) {
            toast.error('Please select at least one winner');
            return;
        }

        setSubmitting(true);
        try {
            await api.post('/goldstars', {
                body: { winner_ids: selectedUserIds, starqty }
            });
            toast.success('Gold stars awarded!');
            history.push('/leaderboards');
        } catch (error) {
            toast.error("Can't post stars, please try again");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        history.goBack();
    };

    const toggleUser = (userId) => {
        setSelectedUserIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleStarQtyUpdate = (qty) => {
        const parsed = parseInt(qty, 10);
        setStarqty(isNaN(parsed) ? 1 : parsed);
    };

    const getDisplayName = (user) => {
        return user.lastname
            ? `${user.firstname} ${user.lastname}`
            : user.firstname;
    };

    if (loading) {
        return <LogoSpinner label="Loading people" />;
    }

    return (
        <PageLayout kind="focus" className="pt-4">
            <form onSubmit={handleSubmit}>
              <Panel title="Issue Gold Stars">

                {/* User Picker */}
                <div className="mb-4">
                    <label className="block text-sm text-on-surface-variant mb-2">
                        Winners ({selectedUserIds.length} selected)
                    </label>
                    <div className="max-h-48 overflow-y-auto bg-gray-900 border border-gray-700 rounded p-2 space-y-1">
                        {users.map(user => (
                            <Checkbox
                                key={user.id}
                                fill
                                checked={selectedUserIds.includes(user.id)}
                                onChange={() => toggleUser(user.id)}
                                label={getDisplayName(user)}
                                className={`p-2 rounded transition-colors ${
                                    selectedUserIds.includes(user.id)
                                        ? 'bg-primary/20 border border-primary/50'
                                        : 'hover:bg-surface-container border border-transparent'
                                }`}
                            />
                        ))}
                    </div>
                </div>

                {/* Star Quantity */}
                <div className="mb-6">
                    <FieldRow label="Stars per Winner">
                        <Select
                            label="Stars per winner"
                            value={String(starqty)}
                            onChange={handleStarQtyUpdate}
                            options={[...Array(19)].map((_, i) => ({ key: String(i + 1), label: String(i + 1) }))}
                        />
                    </FieldRow>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                    <button
                        type="submit"
                        disabled={selectedUserIds.length === 0 || submitting}
                        className={`${buttonClass('filled')} flex-1`}
                    >
                        {submitting ? 'Awarding...' : 'Award Stars'}
                    </button>
                    <button
                        type="button"
                        onClick={handleCancel}
                        className={`${buttonClass('tonal')} flex-1`}
                    >
                        Cancel
                    </button>
                </div>
              </Panel>
            </form>
        </PageLayout>
    );
};

export default GoldStars;
